import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiSearch, FiPlus, FiEdit2, FiTrash2, FiUserPlus, FiToggleLeft, FiToggleRight, FiClock } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { userAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import { ROLE_LABELS } from '../../utils/roles';
import AdminLayout from '../../components/layout/AdminLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';

const ROLE_OPTIONS = [
  { value: 'web_management', label: 'Web Management Staff' },
  { value: 'store_management', label: 'Store Management Staff' },
];

const emptyForm = { name: '', email: '', password: '', role: 'web_management' };

const inputClass = 'w-full px-4 py-2.5 border border-blue-100 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200 text-slate-700';
const labelClass = 'block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5';

const AdminStaffAccounts = () => {
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchUsers = async () => {
    try {
      const res = await userAPI.getAll();
      setUsers(res.data?.data?.users || []);
    } catch {
      toast.error('Failed to load staff accounts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const q = search.toLowerCase().trim();
    setFiltered(!q ? users : users.filter((u) =>
      u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
    ));
  }, [search, users]);

  const openCreateModal = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setForm({ name: user.name, email: user.email, password: '', role: user.role });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.role) {
      toast.error('Name and role are required.');
      return;
    }
    if (!editingUser && (!form.email.trim() || !form.password)) {
      toast.error('Email and password are required for a new account.');
      return;
    }
    setSaving(true);
    try {
      if (editingUser) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await userAPI.update(editingUser._id, payload);
        toast.success('Staff account updated.');
      } else {
        await userAPI.create(form);
        toast.success('Staff account created.');
      }
      setModalOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save staff account.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await userAPI.update(user._id, { isActive: !user.isActive });
      setUsers((prev) => prev.map((u) => u._id === user._id ? { ...u, isActive: !u.isActive } : u));
      toast.success(`${user.name} ${!user.isActive ? 'activated' : 'deactivated'}.`);
    } catch {
      toast.error('Failed to update account status.');
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete the staff account for ${user.name}? This cannot be undone.`)) return;
    try {
      await userAPI.delete(user._id);
      setUsers((prev) => prev.filter((u) => u._id !== user._id));
      toast.success('Staff account deleted.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete staff account.');
    }
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#1e3a8a]">Staff Accounts</h2>
          <p className="text-slate-500 text-sm mt-1">Create login accounts for staff and assign Web or Store Management access</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/login-history"
            className="flex items-center gap-2 px-4 py-2.5 border border-blue-100 text-slate-600 hover:bg-blue-50 rounded-xl text-sm font-semibold transition-colors">
            <FiClock className="w-4 h-4" /> Login Activity
          </Link>
          <button onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#1D4ED8] hover:bg-[#1e3a8a] text-white rounded-xl text-sm font-semibold transition-colors shadow-sm">
            <FiPlus className="w-4 h-4" /> Add Staff Account
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-blue-100 p-4 mb-6 flex gap-4 items-center shadow-sm">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input type="text" placeholder="Search by name or email..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-blue-100 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200 text-slate-700 placeholder-blue-200"
          />
        </div>
        <div className="text-sm text-slate-400 flex-shrink-0">
          {filtered.length} account{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-50/50 border-b border-blue-100">
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Name</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Email</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Role</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden sm:table-cell">Created</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Last Login</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Status</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((user, idx) => (
                  <motion.tr key={user._id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.04 }}
                    className="border-b border-blue-50 hover:bg-blue-50/30 transition-colors">
                    <td className="px-5 py-4 font-semibold text-[#1e3a8a]">{user.name}</td>
                    <td className="px-5 py-4 text-slate-500">{user.email}</td>
                    <td className="px-5 py-4">
                      <span className="px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-100 rounded-full text-xs font-semibold">
                        {ROLE_LABELS[user.role] || user.role}
                      </span>
                    </td>
                    <td className="px-5 py-4 hidden sm:table-cell text-slate-400 text-xs whitespace-nowrap">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell text-slate-400 text-xs whitespace-nowrap">
                      {user.lastLogin ? formatDate(user.lastLogin, 'MMM dd, yyyy h:mm a') : 'Never'}
                    </td>
                    <td className="px-5 py-4">
                      <button onClick={() => handleToggleActive(user)}
                        className={`flex items-center gap-1.5 text-xs font-semibold ${user.isActive ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {user.isActive ? <FiToggleRight className="w-5 h-5" /> : <FiToggleLeft className="w-5 h-5" />}
                        {user.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <button onClick={() => openEditModal(user)}
                          className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                          <FiEdit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(user)}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <FiUserPlus className="w-12 h-12 text-blue-200 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No staff accounts yet</p>
              <p className="text-slate-400 text-sm mt-1">Create a Web Management or Store Management account to get started.</p>
            </div>
          )}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}
        title={editingUser ? 'Edit Staff Account' : 'Add Staff Account'} size="md">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelClass}>Full Name *</label>
            <input className={inputClass} value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div>
            <label className={labelClass}>Email *</label>
            <input type="email" className={inputClass} value={form.email}
              disabled={!!editingUser}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required={!editingUser} />
            {editingUser && <p className="text-xs text-slate-400 mt-1">Email cannot be changed.</p>}
          </div>
          <div>
            <label className={labelClass}>{editingUser ? 'New Password (optional)' : 'Password *'}</label>
            <input type="password" className={inputClass} value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder={editingUser ? 'Leave blank to keep current password' : ''}
              required={!editingUser} minLength={6} />
          </div>
          <div>
            <label className={labelClass}>Access Role *</label>
            <select className={inputClass} value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <button type="submit" disabled={saving}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#1D4ED8] hover:bg-[#1e3a8a] text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50">
            {saving ? 'Saving...' : editingUser ? 'Update Account' : 'Create Account'}
          </button>
        </form>
      </Modal>
    </AdminLayout>
  );
};

export default AdminStaffAccounts;
